import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { apiService } from '../services/api';
import {
  initRecaptchaVerifier,
  linkPhoneNumber,
  sendPhoneVerification,
} from '../services/authService';

interface FirebaseError extends Error {
  code?: string;
}

const SmsVerificationPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { account, refreshUserDetails } = useAccount();
  const [phoneNumber, setPhoneNumber] = useState(account?.unverifiedPhoneNumber || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  // Check if we're using the Firebase Auth Emulator
  const isUsingEmulator = React.useCallback(() => {
    // Check if running on localhost/127.0.0.1 (emulator environment)
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }, []);

  // Automatic redirect if user is already verified
  useEffect(() => {
    if (account && !account.unverifiedPhoneNumber) {
      navigate('/dashboard');
    }
  }, [account, navigate]);

  // This effect initializes and renders the reCAPTCHA verifier,
  // attaching it to the window object to ensure it's a stable singleton.
  // Skip reCAPTCHA initialization if using Firebase Auth Emulator.
  useEffect(() => {
    // Skip reCAPTCHA in emulator environment
    if (isUsingEmulator()) {
      return;
    }

    if (recaptchaContainerRef.current && !window.recaptchaVerifier) {
      recaptchaContainerRef.current.innerHTML = '';
      initRecaptchaVerifier(recaptchaContainerRef.current)
        .then((verifier) => {
          window.recaptchaVerifier = verifier;
          verifier.render();
        })
        .catch((_err) => {
          setError('reCAPTCHA expired. Please try again.');
        });
    }

    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        delete window.recaptchaVerifier;
      }
    };
  }, [isUsingEmulator]);

  // Countdown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [resendCooldown]);

  const handleSendVerificationCode = async () => {
    // Guard against duplicate calls
    if (isLoading) {
      return;
    }

    setError('');
    setSuccess('');
    setIsLoading(true);

    // In emulator mode, reCAPTCHA is not required
    if (!isUsingEmulator() && !window.recaptchaVerifier) {
      setError('reCAPTCHA not initialized. Please wait or refresh the page.');
      setIsLoading(false);
      return;
    }

    try {
      const verifier = window.recaptchaVerifier;
      // biome-ignore lint/suspicious/noExplicitAny: UI decoupling prevents importing ApplicationVerifier
      const newVerificationId = await sendPhoneVerification(phoneNumber, verifier as any);
      setVerificationId(newVerificationId);
      setSuccess('Verification code sent successfully!');
      setResendCooldown(60);
    } catch (err: unknown) {
      const error = err as FirebaseError;

      if (error.code === 'auth/operation-not-allowed') {
        setError('Phone authentication is not enabled. Please contact support.');
      } else if (error.code === 'auth/invalid-phone-number') {
        setError(
          'Invalid phone number format. Please use international format (e.g., +1234567890).'
        );
      } else if (error.code === 'auth/quota-exceeded') {
        setError('SMS quota exceeded. Please try again later or contact support.');
      } else if (error.code === 'auth/captcha-check-failed') {
        setError('reCAPTCHA verification failed. Please refresh the page and try again.');
      } else if (error.code === 'auth/missing-phone-number') {
        setError('Please enter a phone number.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a few minutes and try again.');
      } else if (error.code === 'auth/admin-restricted-operation') {
        setError('Phone verification is restricted. Please contact support.');
      } else {
        setError(
          `Failed to send verification code: ${error.message || 'Please check the phone number and try again.'}`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (!verificationId) {
      setError('Please send a verification code first.');
      setIsLoading(false);
      return;
    }

    try {
      await linkPhoneNumber(verificationId, verificationCode);
      await apiService.clearUnverifiedPhoneNumber();
      // refreshUserDetails is async in implementation, so we await it to ensure state is updated
      await refreshUserDetails();
      setSuccess('Phone number verified successfully! Redirecting...');
      navigate('/dashboard');
    } catch (err: unknown) {
      const error = err as FirebaseError;

      if (error.code === 'auth/credential-already-in-use') {
        setError(
          'This phone number is already linked to another account. Please use a different number.'
        );
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setError(
          'This phone number is already associated with an account that uses a different sign-in method (e.g., Google, Microsoft). Please log in using that method.'
        );
      } else {
        setError('Failed to verify code. Please check the code and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <img
            src="/Bidlogiq_Logo-Long_Full colours.png"
            alt="BIDLOGIQ Logo"
            style={{ maxWidth: '300px', marginBottom: '24px' }}
          />
          <Typography variant="h5" component="h1" gutterBottom>
            SMS Verification
          </Typography>
          <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
            Please confirm your phone number and click "Send Code" to receive a verification
            message.
          </Typography>
          <Box component="form" noValidate sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="phoneNumber"
              label="Phone Number"
              name="phoneNumber"
              autoComplete="tel"
              value={phoneNumber}
              error={phoneNumber.length > 0 && !/^\+[1-9]\d{1,14}$/.test(phoneNumber)}
              helperText={
                phoneNumber.length > 0 && !/^\+[1-9]\d{1,14}$/.test(phoneNumber)
                  ? 'Invalid phone number format. Please use international format (e.g., +1234567890).'
                  : 'Enter your phone number in international format (e.g., +1234567890).'
              }
              onChange={(e) => {
                // Strip spaces, dashes, and parentheses to ensure clean E.164-friendly format
                const cleaned = e.target.value.replace(/[^\d+]/g, '');
                setPhoneNumber(cleaned);
              }}
              disabled={isLoading || !!verificationId}
            />

            <Box ref={recaptchaContainerRef} />

            <Button
              type="button"
              fullWidth
              variant="contained"
              sx={{ mt: 1, mb: 2 }}
              onClick={handleSendVerificationCode}
              disabled={
                isLoading ||
                !/^\+[1-9]\d{1,14}$/.test(phoneNumber) ||
                resendCooldown > 0 ||
                !!verificationId
              }
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Send Verification Code'}
            </Button>

            {verificationId && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="verificationCode"
                  label="Verification Code"
                  name="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.trim())}
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  onClick={handleVerifyCode}
                  disabled={isLoading || !verificationCode}
                >
                  {isLoading ? <CircularProgress size={24} /> : 'Verify Code'}
                </Button>
              </>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
                {success}
              </Alert>
            )}
          </Box>
          <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider', width: '100%' }}>
            <Typography variant="body2" color="text.primary">
              Having trouble? Contact support:
            </Typography>
            <Typography variant="body2" color="text.primary">
              Email: <a href="mailto:support@bidlogiq.ai">support@bidlogiq.ai</a>
            </Typography>
            <Typography variant="body2" color="text.primary">
              Phone: <a href="tel:+61721436035">+61 (0)7 2143 6035</a>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SmsVerificationPage;
